/**
 * Entry points into the improvement conversation (IMP-BR-6): the quiet "That wasn't right" button
 * under an agent-authored message or a held draft, and the case header's "Improve the agent".
 * Both only call the provider's single opener.
 */

import { MessageSquare } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../ui/button';
import { StatusBadge } from '../../layout/StatusBadge';
import { caseFeedbackQueryKey, listCaseFeedback, type LandlordFeedbackView } from '../../../api/agent-feedback.api';
import {
  useImprovementConversationOpener,
  type OpenConversationRequest,
} from './ImprovementConversationProvider';

export interface ImprovementEntryButtonProps {
  request: OpenConversationRequest;
  'data-testid': string;
}

/** "That wasn't right" — ghost button with a speech bubble, opening the panel on its anchor. */
export function ImprovementEntryButton({ request, 'data-testid': testId }: ImprovementEntryButtonProps) {
  const openConversation = useImprovementConversationOpener();
  return (
    <Button
      type="button"
      variant="ghost"
      className="gap-1.5 px-2.5 text-[13px] text-foreground"
      onClick={() => openConversation(request)}
      data-testid={testId}
    >
      <MessageSquare aria-hidden="true" />
      <span>That wasn't right</span>
    </Button>
  );
}

export interface MessageFeedbackControlsProps {
  /** The CaseEvent id of the agent-authored message. */
  eventId: string;
  /** The landlord's live report about this message, if any. */
  report: LandlordFeedbackView | undefined;
  request: OpenConversationRequest;
}

/**
 * The controls under an agent-authored message: entry 1 of IMP-BR-6 on every agent-authored message,
 * reported or not, plus the Reported chip once the landlord has a report on it. Activating the entry
 * again reopens the conversation open on that anchor (the start answers `existing: true`, IMP-BR-5);
 * this is the only way in, so a reported message has no second opener.
 */
export function MessageFeedbackControls({ eventId, report, request }: MessageFeedbackControlsProps) {
  return (
    <>
      {report && <StatusBadge status="reported" className="rounded-full" data-testid={`agent-feedback-reported-${eventId}`} />}
      <ImprovementEntryButton data-testid={`improve-agent-message-${eventId}`} request={request} />
    </>
  );
}

export interface ImproveAgentButtonProps {
  caseId: string;
  request: OpenConversationRequest;
  className?: string;
}

/**
 * "Improve the agent" in a case header. Renders only when the case has had an agent run (the
 * `eligible` flag of GET /landlord/api/cases/:id/agent-feedback); nothing renders while that loads.
 */
export function ImproveAgentButton({ caseId, request, className }: ImproveAgentButtonProps) {
  const openConversation = useImprovementConversationOpener();
  const { data } = useQuery({
    queryKey: caseFeedbackQueryKey(caseId),
    queryFn: () => listCaseFeedback(caseId),
    // Eligibility flips the moment the case's first agent run is processed, so the header re-checks
    // whenever the page is revisited rather than serving a cached "no" (IMP-BR-6 entry 3).
    staleTime: 0,
  });
  if (!data?.eligible) return null;
  return (
    <Button
      type="button"
      variant="outline"
      className={className ?? 'font-semibold'}
      onClick={() => openConversation(request)}
      data-testid="improve-agent"
    >
      Improve the agent
    </Button>
  );
}
